import { Auth } from 'aws-amplify';
import { clearActiveFleetId } from './fleetStorage';

const AUTH_BROADCAST_CHANNEL = 'varroc-client-auth';
const LOGOUT_MESSAGE = 'LOGOUT';
const COGNITO_STORAGE_PREFIX = 'CognitoIdentityServiceProvider';

type AuthStateCleaner = () => void;
type SignOutHandler = () => void | Promise<void>;

let logoutInProgress = false;
let authStateCleaner: AuthStateCleaner = () => {
  clearActiveFleetId();
};
let signOutHandler: SignOutHandler | null = null;
let broadcastChannel: BroadcastChannel | null = null;

function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }

  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
  }

  return broadcastChannel;
}

export function registerAuthStateCleaner(cleaner: AuthStateCleaner): () => void {
  authStateCleaner = cleaner;

  return () => {
    authStateCleaner = () => {
      clearActiveFleetId();
    };
  };
}

export function registerSignOutHandler(handler: SignOutHandler): () => void {
  signOutHandler = handler;

  return () => {
    if (signOutHandler === handler) {
      signOutHandler = null;
    }
  };
}

export function clearClientAuthState(): void {
  authStateCleaner();
}

export function broadcastLogout(): void {
  getBroadcastChannel()?.postMessage({ type: LOGOUT_MESSAGE });
}

export async function performLogout(options?: { broadcast?: boolean }): Promise<void> {
  if (logoutInProgress) {
    return;
  }

  logoutInProgress = true;

  try {
    clearClientAuthState();

    if (options?.broadcast !== false) {
      broadcastLogout();
    }

    if (signOutHandler) {
      await signOutHandler();
    } else {
      await Auth.signOut();
    }
  } finally {
    window.setTimeout(() => {
      logoutInProgress = false;
    }, 1000);
  }
}

function ignorePromiseRejection(): undefined {
  return undefined;
}

export function triggerLogout(options?: { broadcast?: boolean }): void {
  performLogout(options).then(undefined, ignorePromiseRejection);
}

export function handleUnauthorized(): void {
  triggerLogout();
}

function isCognitoStorageKey(key: string | null): boolean {
  return !!key && key.startsWith(COGNITO_STORAGE_PREFIX);
}

export function subscribeToCrossTabLogout(onLogout: () => void): () => void {
  const handleRemoteLogout = (): void => {
    if (logoutInProgress) {
      return;
    }

    clearClientAuthState();
    onLogout();
  };

  const onStorage = (event: StorageEvent): void => {
    if (isCognitoStorageKey(event.key) && event.newValue === null) {
      handleRemoteLogout();
    }
  };

  const channel = getBroadcastChannel();
  const onBroadcast = (event: MessageEvent): void => {
    if (event.data?.type === LOGOUT_MESSAGE) {
      handleRemoteLogout();
    }
  };

  window.addEventListener('storage', onStorage);
  channel?.addEventListener('message', onBroadcast);

  return () => {
    window.removeEventListener('storage', onStorage);
    channel?.removeEventListener('message', onBroadcast);
  };
}
